import {
  Autocomplete,
  TextField,
  CircularProgress,
  Box,
  Typography,
  Chip,
  Paper,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import useDebounce from "hooks/useDebounce";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useBoundStore } from "useStore";
import useCampaignQuery from "../Queries/CampaignQuery";

/* -------------------- Inferencia inteligente -------------------- */
const inferSearchItem = (rawValue, isAdsView) => {
  const value = rawValue.trim();
  const upper = value.toUpperCase();

  // Campaña hide if is ads view
  if (value.includes(" ")) {
    //if is ads view, hide campaign name from options
    if (!isAdsView) {
      return { value, type: "Campaña", label: value };
    }
  }

  // MLA hide if is ads view
  if (/^ML[A-Z]\d{2,}$/.test(upper)) {
    //if is ads view, hide mla from options
    if (!isAdsView) {
      return { value: upper, type: "MLA", label: upper };
    }
  }

  // SKU
  if (/^[A-Z0-9][A-Z0-9._-]{2,}$/.test(upper) && /[0-9._-]/.test(upper)) {
    return { value: upper, type: "SKU", label: upper };
  }

  // Texto libre hide if is ads view
  if (!isAdsView) {
    return { value, type: "Campaña", label: value };
  }

  // Texto libre for ads view
  return { value, type: "Título", label: value };
};

export default function SearchBar() {
  const selectedCampaign = useBoundStore((state) => state.selectedCampaign);
  const isAdsView = !!selectedCampaign;
  const setSearch = useBoundStore((s) => s.setCampaignManagerSearch);
  const currentSearch = useBoundStore((s) => s.campaignManagerSearch);

  const { data: campaignData, isLoading } = useCampaignQuery();

  const [inputValue, setInputValue] = useState("");
  const debouncedInput = useDebounce(inputValue, 300);
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);

  const items = useMemo(() => {
    return Array.isArray(campaignData?.data) ? campaignData.data : [];
  }, [campaignData]);

  const selectedValues = useMemo(() => {
    if (!Array.isArray(currentSearch)) return [];
    return currentSearch.map((v) => (typeof v === "string" ? v : v.value));
  }, [currentSearch]);

  const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);

  useEffect(() => {
    if (!debouncedInput.trim() || !items.length) {
      setOptions([]);
      return;
    }

    const searchLower = debouncedInput.toLowerCase();
    const matched = [];
    const seen = new Set();
    const MAX_RESULTS = 100;

    for (let i = 0; i < items.length && matched.length < MAX_RESULTS; i++) {
      const campaign = items[i];

      const addOption = (val, type) => {
        if (!val) return;
        const key = `${type}-${val}`;
        if (seen.has(key) || selectedSet.has(val)) return;
        seen.add(key);
        matched.push({
          value: val,
          type,
          label:
            type === "Título" && val.length > 70
              ? val.slice(0, 70) + "..."
              : val,
        });
      };

      if (
        String(campaign.campaignName || "")
          .toLowerCase()
          .includes(searchLower)
      ) {
        //if is ads view, hide campaign name from options
        if (!isAdsView) {
          addOption(campaign.campaignName, "Campaña");
        }
      }

      if (
        String(campaign.sku || "")
          .toLowerCase()
          .includes(searchLower)
      ) {
        addOption(campaign.sku, "SKU");
      }

      for (const ad of campaign.ads || []) {
        if (matched.length >= MAX_RESULTS) break;

        if (
          String(ad.itemId || "")
            .toLowerCase()
            .includes(searchLower)
        ) {
          //if is ads view, hide mla from options
          if (!isAdsView) {
            addOption(ad.itemId, "MLA");
          }
        }
        if (
          String(ad.sku || "")
            .toLowerCase()
            .includes(searchLower)
        ) {
          addOption(ad.sku, "SKU");
        }
        if (
          String(ad.title || "")
            .toLowerCase()
            .includes(searchLower)
        ) {
          addOption(ad.title, "Título");
        }
      }
    }

    setOptions(matched);
  }, [debouncedInput, items, selectedSet]);

  /* -------------------- Enter -------------------- */
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key !== "Enter") return;
      const highlighted = document.activeElement?.getAttribute(
        "aria-activedescendant",
      );
      if (!highlighted && inputValue.trim()) {
        const newItem = inferSearchItem(inputValue, isAdsView);
        if (!selectedSet.has(newItem.value)) {
          const arr = Array.isArray(currentSearch) ? currentSearch : [];
          setSearch([...arr, newItem]);
        }
        setInputValue("");
        e.preventDefault();
      }
    },
    [inputValue, selectedSet, currentSearch, setSearch],
  );

  const handleChange = useCallback(
    (_, newValue) => {
      const normalized = newValue
        .map((v) => {
          if (typeof v === "string") return inferSearchItem(v, isAdsView);
          return {
            value: v.value.trim(),
            type: v.type || "Campaña",
            label: v.label || v.value,
          };
        })
        .filter(Boolean);
      setSearch(normalized);
    },
    [setSearch],
  );

  const handleInputChange = useCallback((_, newInputValue) => {
    setInputValue(newInputValue);
    setOpen(newInputValue.trim().length > 0);
  }, []);

  const getOptionLabel = useCallback((option) => {
    if (typeof option === "string") return option;
    return option?.label || "";
  }, []);

  const renderTags = useCallback(() => null, []);

  const renderInput = useCallback(
    (params) => (
      <TextField
        {...params}
        label="Buscar"
        placeholder={`${isAdsView ? "SKU, Titulo..." : "SKU, MLA..."}`}
        onKeyDown={handleKeyDown}
        InputProps={{
          ...params.InputProps,
          startAdornment: (
            <>
              <SearchIcon fontSize="small" sx={{ mr: 1 }} />
              {params.InputProps.startAdornment}
            </>
          ),
          endAdornment: (
            <>
              {isLoading && <CircularProgress size={20} color="inherit" />}
              {params.InputProps.endAdornment}
            </>
          ),
        }}
        sx={{ width: { xs: "150px", sm: "200px", md: "300px" } }}
      />
    ),
    [handleKeyDown, isLoading],
  );

  const renderOption = useCallback((props, option) => {
    const { key, ...other } = props;
    const colors = {
      SKU: ["#e3f2fd", "#1976d2"],
      MLA: ["#e1fcef", "#1b5e20"],
      Campaña: ["#f3e5f5", "#7b1fa2"],
      Título: ["#fff3e0", "#e65100"],
    };
    const [bg, text] = colors[option.type] || ["#eee", "#555"];

    return (
      <Box component="li" key={key} {...other}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Typography variant="body2" sx={{ flex: 1 }}>
            {option.label}
          </Typography>
          <Chip
            label={option.type}
            size="small"
            sx={{
              ml: 1,
              height: 20,
              fontSize: "0.7rem",
              backgroundColor: bg,
              color: text,
            }}
          />
        </Box>
      </Box>
    );
  }, []);

  const CustomPaper = useCallback(
    (props) => {
      const showEmpty =
        inputValue.trim().length > 0 &&
        debouncedInput.trim().length > 0 &&
        options.length === 0 &&
        !isLoading &&
        open;

      return (
        <Paper {...props}>
          {showEmpty ? (
            <Box p={2} textAlign="center">
              <Typography variant="body2">
                No se encontraron resultados
              </Typography>
            </Box>
          ) : (
            props.children
          )}
        </Paper>
      );
    },
    [inputValue, debouncedInput, options.length, isLoading, open],
  );

  return (
    <Autocomplete
      multiple
      freeSolo
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      value={selectedValues}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      filterSelectedOptions
      getOptionLabel={getOptionLabel}
      renderOption={renderOption}
      renderTags={renderTags}
      renderInput={renderInput}
      isOptionEqualToValue={(option, value) => {
        const optVal = typeof option === "object" ? option.value : option;
        const selVal = typeof value === "object" ? value?.value : value;
        return optVal === selVal;
      }}
      filterOptions={(opts) => opts}
      noOptionsText="No se encontraron resultados"
      PaperComponent={CustomPaper}
      ListboxProps={{ style: { maxHeight: "300px" } }}
      componentsProps={{
        paper: { sx: { boxShadow: "none !important" } },
        popper: { sx: { boxShadow: "none !important" } },
      }}
    />
  );
}
